import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pipApi } from '../../services/api';
import { Spinner, Modal } from '../../components/shared/index';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const GOAL_STATUS = { pending:'bg-gray-100 text-gray-600', in_progress:'bg-blue-100 text-blue-700', achieved:'bg-green-100 text-green-700', not_achieved:'bg-red-100 text-red-700' };

export default function PipDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const { data: pip, isLoading } = useQuery({ queryKey: ['pip', id], queryFn: () => pipApi.get(id).then(r => r.data) });
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [showCheckin, setShowCheckin]   = useState(false);
  const [showClose, setShowClose]       = useState(false);
  const [goalForm, setGoalForm]         = useState({ title:'', success_metric:'', due_date:'' });
  const [checkinForm, setCheckinForm]   = useState({ progress_notes:'', overall_status:'on_track' });
  const [closeForm, setCloseForm]       = useState({ final_outcome:'recovered' });

  const addGoalMut   = useMutation({ mutationFn: d => pipApi.addGoal(id,d), onSuccess: () => { qc.invalidateQueries(['pip',id]); setShowGoalForm(false); toast.success('Goal added'); } });
  const checkinMut   = useMutation({ mutationFn: d => pipApi.checkIn(id,d), onSuccess: () => { qc.invalidateQueries(['pip',id]); setShowCheckin(false); toast.success('Check-in recorded'); } });
  const closeMut     = useMutation({ mutationFn: d => pipApi.close(id,d), onSuccess: () => { qc.invalidateQueries(['pip',id]); setShowClose(false); toast.success('PIP closed'); } });
  const goalUpdateMut= useMutation({ mutationFn: ({gid,d}) => pipApi.updateGoal(id,gid,d), onSuccess: () => qc.invalidateQueries(['pip',id]) });

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;
  if (!pip)      return <p className="p-6 text-red-600">PIP not found</p>;

  const daysLeft = Math.ceil((new Date(pip.end_date)-new Date())/86400000);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Link to="/pips" className="text-sm text-blue-600 hover:underline mb-4 inline-block">← PIPs</Link>
      <div className="card p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{pip.employee?.name}</h1>
            <p className="text-sm text-gray-500 mt-1 capitalize">{pip.employee?.role?.replace('_',' ')} · {pip.employee?.band}</p>
            <div className="flex gap-3 mt-3 flex-wrap text-sm">
              <span><b>Start:</b> {pip.start_date}</span>
              <span><b>End:</b> {pip.end_date}</span>
              {pip.status === 'active' && <span className={clsx('font-semibold', daysLeft < 15 ? 'text-red-600' : 'text-gray-700')}>{daysLeft > 0 ? `${daysLeft} days left` : 'Overdue'}</span>}
              <span className="capitalize"><b>Root cause:</b> {pip.root_cause?.replace('_',' ') || '—'}</span>
            </div>
          </div>
          {pip.status === 'active' && (
            <div className="flex gap-2">
              <button onClick={() => setShowCheckin(true)} className="btn-secondary btn-sm">Add check-in</button>
              <button onClick={() => setShowClose(true)}   className="btn-danger btn-sm">Close PIP</button>
            </div>
          )}
        </div>
        {pip.notes && <p className="text-sm text-gray-600 mt-4 p-3 bg-gray-50 rounded-lg">{pip.notes}</p>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Goals */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-800">Improvement goals</h2>
            {pip.status === 'active' && <button onClick={() => setShowGoalForm(true)} className="btn-secondary btn-sm">+ Add goal</button>}
          </div>
          <div className="space-y-3">
            {pip.goals?.length === 0 && <p className="text-gray-400 text-sm">No goals yet</p>}
            {pip.goals?.map(g => (
              <div key={g.id} className="border border-gray-100 rounded-lg p-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{g.title}</p>
                    <p className="text-xs text-gray-500 mt-1">Success: {g.success_metric}</p>
                    {g.due_date && <p className="text-xs text-gray-400">Due: {g.due_date}</p>}
                  </div>
                  <span className={clsx('shrink-0 px-2 py-0.5 rounded-full text-xs font-medium', GOAL_STATUS[g.status]||'bg-gray-100 text-gray-600')}>{g.status?.replace('_',' ')}</span>
                </div>
                {pip.status === 'active' && (
                  <div className="flex gap-2 mt-2">
                    {['in_progress','achieved','not_achieved'].map(s => (
                      <button key={s} onClick={() => goalUpdateMut.mutate({gid:g.id,d:{status:s}})}
                        className={clsx('px-2 py-0.5 text-xs rounded border transition-colors', g.status===s?'bg-blue-600 text-white border-blue-600':'border-gray-200 text-gray-500 hover:bg-gray-50')}>
                        {s.replace('_',' ')}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Check-ins */}
        <div className="card p-5">
          <h2 className="font-semibold text-gray-800 mb-4">Check-in log</h2>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {pip.checkins?.length === 0 && <p className="text-gray-400 text-sm">No check-ins yet</p>}
            {pip.checkins?.map(c => (
              <div key={c.id} className="border-l-4 border-blue-200 pl-3 py-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', c.overall_status==='on_track'?'bg-green-100 text-green-700':c.overall_status==='at_risk'?'bg-yellow-100 text-yellow-700':'bg-red-100 text-red-700')}>{c.overall_status?.replace('_',' ')}</span>
                  <span className="text-xs text-gray-400">{new Date(c.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-sm text-gray-700">{c.progress_notes}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add goal modal */}
      <Modal open={showGoalForm} onClose={() => setShowGoalForm(false)} title="Add improvement goal">
        <form onSubmit={e=>{e.preventDefault();addGoalMut.mutate(goalForm)}} className="space-y-4">
          <div><label className="label">Goal title *</label><input required className="input" value={goalForm.title} onChange={e=>setGoalForm(f=>({...f,title:e.target.value}))} /></div>
          <div><label className="label">Success metric *</label><input required className="input" value={goalForm.success_metric} onChange={e=>setGoalForm(f=>({...f,success_metric:e.target.value}))} placeholder="How will we know this goal is achieved?" /></div>
          <div><label className="label">Due date</label><input type="date" className="input" value={goalForm.due_date} onChange={e=>setGoalForm(f=>({...f,due_date:e.target.value}))} /></div>
          <div className="flex justify-end gap-3"><button type="button" onClick={()=>setShowGoalForm(false)} className="btn-secondary">Cancel</button><button type="submit" className="btn-primary">Add goal</button></div>
        </form>
      </Modal>

      {/* Check-in modal */}
      <Modal open={showCheckin} onClose={()=>setShowCheckin(false)} title="Add PIP check-in">
        <form onSubmit={e=>{e.preventDefault();checkinMut.mutate(checkinForm)}} className="space-y-4">
          <div><label className="label">Progress notes *</label><textarea required className="input h-24 resize-none" value={checkinForm.progress_notes} onChange={e=>setCheckinForm(f=>({...f,progress_notes:e.target.value}))} /></div>
          <div><label className="label">Overall status</label>
            <select className="input" value={checkinForm.overall_status} onChange={e=>setCheckinForm(f=>({...f,overall_status:e.target.value}))}>
              {['on_track','at_risk','failing'].map(s=><option key={s} value={s}>{s.replace('_',' ')}</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-3"><button type="button" onClick={()=>setShowCheckin(false)} className="btn-secondary">Cancel</button><button type="submit" className="btn-primary">Record check-in</button></div>
        </form>
      </Modal>

      {/* Close PIP modal */}
      <Modal open={showClose} onClose={()=>setShowClose(false)} title="Close PIP">
        <form onSubmit={e=>{e.preventDefault();closeMut.mutate(closeForm)}} className="space-y-4">
          <div><label className="label">Final outcome</label>
            <select className="input" value={closeForm.final_outcome} onChange={e=>setCloseForm(f=>({...f,final_outcome:e.target.value}))}>
              <option value="recovered">Recovered — return to normal cycle</option>
              <option value="extended">Extended — 15 more days</option>
              <option value="exit_recommended">Exit recommended</option>
            </select>
          </div>
          <div className="flex justify-end gap-3"><button type="button" onClick={()=>setShowClose(false)} className="btn-secondary">Cancel</button><button type="submit" className="btn-danger">Close PIP</button></div>
        </form>
      </Modal>
    </div>
  );
}
